import { groupBy, isNumber } from 'lodash';
import { Transport, now, Frequency, TransportTime } from 'tone';
import { BassHit, MarchSubscriber, TB303Waveform } from '../core';
import { nativeAudioContext, wetDest } from './transport';

export const F_MOD_MIN = 0;
export const F_MOD_MAX = 1;
export const F_MOD_STEP = 0.01;
export const CUTOFF_MIN = 100;
export const CUTOFF_MAX = 2000;
export const CUTOFF_STEP = 1;
export const Q_MIN = 0;
export const Q_MAX = 15;
export const Q_STEP = 0.1;
export const DECAY_MIN = 0.0;
export const DECAY_MAX = 1.0;
export const DECAY_STEP = 0.001;
export const ACCENT_MIN = 0;
export const ACCENT_MAX = 1;
export const ACCENT_STEP = 0.01;

const Q_MAX_SCALE = [8, 25];
const CUTOFF_ENV_MOD = 2000;

let fModAmount = 0.7;

let fModDecay = 0.5;

let maxGain = 0.9;
let cutoff = 2500;
let Q = 8.5;
let accentStrength = 0.5;

let lastStart = -1;

let osc = nativeAudioContext.createOscillator();
osc.type = 'sawtooth';
let amp = nativeAudioContext.createGain();
amp.gain.value = 0;
let filter1 = nativeAudioContext.createBiquadFilter();
filter1.type = 'lowpass';
filter1.frequency.value = cutoff;
filter1.Q.value = Q;
let filter2 = nativeAudioContext.createBiquadFilter();
filter2.type = 'lowpass';
filter2.frequency.value = cutoff;
filter2.Q.value = Q;
let lpf = nativeAudioContext.createBiquadFilter();
lpf.type = 'lowpass';
lpf.frequency.value = 6000;
lpf.Q.value = 0;
let distortWet = nativeAudioContext.createGain();
distortWet.gain.value = 0.5;
let distortDry = nativeAudioContext.createGain();
distortDry.gain.value = 0.5;
let distort = nativeAudioContext.createWaveShaper();
distort.curve = makeDistortionCurve(0.1);
distort.oversample = 'none';

osc.connect(amp);
amp.connect(filter1);
filter1.connect(filter2);
filter2.connect(lpf);
lpf.connect(distortWet);
lpf.connect(distortDry);
distortWet.connect(distort);
distortDry.connect(wetDest);
distort.connect(wetDest);
distort.connect(wetDest);

let currentSchedule: number | null = null;
let currentSequence: { [timeStep: string]: BassHit[] } = {};
let currentSequenceLength = 16;
let marchSubscribers: MarchSubscriber[] = [];

osc.start();

export function applySequence(seq: readonly BassHit[], seqLength: number) {
    currentSequence = groupBy(seq, s => s.timeStep);
    currentSequenceLength = seqLength;
}

export function setWaveform(waveform: TB303Waveform) {
    osc.type = waveform;
}

export function setCutOffFreq(cutOffFreq: number) {
    let rel = (cutOffFreq - CUTOFF_MIN) / (CUTOFF_MAX - CUTOFF_MIN);
    let pwRel = rel * rel;

    cutoff = CUTOFF_MIN + pwRel * (CUTOFF_MAX - CUTOFF_MIN);
    filter1.frequency.setTargetAtTime(cutoff, now(), 0.01);
    filter2.frequency.setTargetAtTime(cutoff, now(), 0.01);

    let cutOffRel = (cutoff - CUTOFF_MIN) / (CUTOFF_MAX - CUTOFF_MIN);
    let QRel = (Q - Q_MIN) / (Q_MAX - Q_MIN);
    let QMaxActual = Q_MAX_SCALE[0] + cutOffRel * (Q_MAX_SCALE[1] - Q_MAX_SCALE[0]);
    let QActual = Q_MIN + (QMaxActual - Q_MIN) * QRel;
    filter1.Q.setTargetAtTime(QActual, now(), 0.01);
    filter2.Q.setTargetAtTime(QActual, now(), 0.01);
}
export function setResonance(resonance: number) {
    Q = resonance;
    let QRel = (Q - Q_MIN) / (Q_MAX - Q_MIN);
    let cutOffRel = (cutoff - CUTOFF_MIN) / (CUTOFF_MAX - CUTOFF_MIN);
    let QMaxActual = Q_MAX_SCALE[0] + cutOffRel * (Q_MAX_SCALE[1] - Q_MAX_SCALE[0]);
    let QActual = Q_MIN + (QMaxActual - Q_MIN) * QRel;
    filter1.Q.setTargetAtTime(QActual, now(), 0.01);
    filter2.Q.setTargetAtTime(QActual, now(), 0.01);
}
export function setEnvMod(envMod: number) {
    fModAmount = envMod;
}
export function setDecay(decay: number) {
    fModDecay = decay;
}
export function setAccent(accent: number) {
    accentStrength = accent;
}

export function start(startTime: number, startBeat: number) {
    if (isNumber(currentSchedule)) return;
    let step = startBeat;
    let heldFrequency: number | null = null, heldGain: number | null = null;
    currentSchedule = Transport.scheduleRepeat(time => {

        let hits = currentSequence[getSubDiv(step)] ?? [];
        for (let { slide, accent, note, octave } of hits) {
            if (lastStart >= time) continue;

            let frequency = Frequency(`${note}${octave}`).toFrequency();
            let velocity = accent ? 0.3 + 0.65 * accentStrength : 0.3;
            let peakGain = velocity * maxGain;
            if (heldFrequency) {
                osc.frequency.setValueAtTime(heldFrequency, time);
                osc.frequency.exponentialRampToValueAtTime(frequency, time + 0.04);
                amp.gain.setValueAtTime(heldGain!, time);
            } else {
                osc.frequency.setValueAtTime(frequency, time);
                amp.gain.setValueAtTime(0, time);
            }
            amp.gain.linearRampToValueAtTime(peakGain, time + 0.003);
            if (slide && currentSequence[getSubDiv(step + 1)]?.length > 0) {
                heldFrequency = frequency;
                heldGain = peakGain;
            } else {
                heldFrequency = null;
                heldGain = null;
                let r = TransportTime('32n').toSeconds();
                amp.gain.setValueAtTime(peakGain, time + r);
                amp.gain.exponentialRampToValueAtTime(0.0001, time + 2 * r - 0.01);
            }

            let baseFreq = cutoff;
            let fModPeak = baseFreq + fModAmount * CUTOFF_ENV_MOD * (accent ? 1 : 1);
            let effectiveDecay = fModDecay * TransportTime('16n').toSeconds();

            filter1.frequency.cancelScheduledValues(time);
            filter2.frequency.cancelScheduledValues(time);
            filter1.frequency.setValueAtTime(baseFreq, time);
            filter2.frequency.setValueAtTime(baseFreq, time);
            filter1.frequency.linearRampToValueAtTime(fModPeak, time + 0.003);
            filter2.frequency.linearRampToValueAtTime(fModPeak, time + 0.003);
            filter1.frequency.setValueAtTime(fModPeak, time + 0.003);
            filter2.frequency.setValueAtTime(fModPeak, time + 0.003);
            filter1.frequency.linearRampToValueAtTime(baseFreq, time + 0.003 + effectiveDecay);
            filter2.frequency.linearRampToValueAtTime(baseFreq, time + 0.003 + effectiveDecay);

            lastStart = time;
        }
        if (marchSubscribers.length) {
            let hitNotes = hits.map(h => h.note);
            let subDiv = getSubDiv(step);
            let prevSubDiv = getSubDiv(step - 1);
            for (let sub of marchSubscribers) {
                sub(subDiv, prevSubDiv, hitNotes, time);
            }
        }
        step++;
    }, '16n', startTime);

    amp.gain.setValueAtTime(0, now());
}

function getSubDiv(step: number) {
    let subDiv = step % currentSequenceLength;
    if (subDiv < 0) subDiv += currentSequenceLength;
    return subDiv;
}

export function stop() {
    if (isNumber(currentSchedule)) {
        amp.gain.setTargetAtTime(0, now(), 0.01);

        Transport.clear(currentSchedule);
        currentSchedule = null;
        for (let sub of marchSubscribers) {
            sub(-1, -1, [], now());
        }
    }

}


export function subscribeToMarch(subscriber: MarchSubscriber) {
    marchSubscribers.push(subscriber);
    return () => {
        let idx = marchSubscribers.indexOf(subscriber);
        if (idx >= 0) {
            marchSubscribers.splice(idx, 1);
        }
    }
}

function makeDistortionCurve(amount: number) {
    let k = amount * 100,
        nSamples = 4096,
        curve = new Float32Array(nSamples),
        deg = Math.PI / 180,
        i = 0;
    for (; i < nSamples; ++i) {
        let normalized = (i / (nSamples - 1)) * 2 - 1;
        if (Math.abs(normalized) < 0.001) {
            // should output 0 when input is 0
            curve[i] = 0;
        } else {
            curve[i] = (3 + k) * normalized * 20 * deg / (Math.PI + k * Math.abs(normalized));
        }
    }
    return curve;
}